/**
 * A plain-text email, tidied for reading: the message itself, its signature, and the earlier thread it quotes (shown
 * folded). Mail clients mark the start of a quote in their own language, so the markers cover English, Portuguese,
 * Italian, Spanish, French and German.
 */

interface EmailText {
  /** What the sender wrote. */
  body: string;
  /** After a `-- ` line, if any. */
  signature: string;
  /** The earlier messages it quotes, from the first quote marker on. */
  quoted: string;
}

/** "------ Original Message ------", "-----Mensagem original-----", forwarded ones too. */
const SEPARATOR =
  /^\s*-{2,}\s*(original message|messaggio originale|mensagem original|mensaje original|message d'origine|ursprüngliche nachricht|forwarded message|messaggio inoltrato|mensagem encaminhada|mensaje reenviado|message transféré|weitergeleitete nachricht)\s*-{2,}\s*$/i;
/** "On Mon, 5 Oct 2026, Ana <ana@x> wrote:", "Em 5 de out., Ana escreveu:", "Il giorno … ha scritto:". */
const WROTE =
  /^\s*(on|em|il|el|le|am)\b.{4,300}\b(wrote|escreveu|ha scritto|scrisse|escribió|a écrit|schrieb)\s*:\s*$/i;
/** An Outlook-style header block: "From: …" with "Sent:"/"Date:" in the next few lines. */
const FROM = /^\s*\*?(from|de|da|von)\s*:\*?\s+\S/i;
const SENT = /^\s*\*?(sent|date|enviad[oa](?: em)?|data|inviato|envoyé|gesendet|fecha)\s*:\*?\s/i;
/** How far below "From:" its date line may be. */
const HEADER_SPAN = 4;
/** A line of underscores Outlook puts above the quoted headers. */
const RULE = /^\s*_{8,}\s*$/;
/** Inline-image placeholders plain text keeps ("[cid:image001.png@01DC…]"). */
const CID = /\[cid:[^\]]*\]/gi;
/** "ana@x.com<mailto:ana@x.com>": the address twice. */
const MAILTO = /<mailto:[^>\s]+>/gi;
/** A line's quote marks ("> > "). */
const QUOTE_MARK = /^(>\s?)+/;
const QUOTE_MARKS = /^(>\s?)+/gm;

/** Where the quoted thread starts (a line index), or -1. */
function quoteStart(lines: readonly string[]): number {
  // Markers may themselves be quoted ("> On … wrote:"), in a thread forwarded whole.
  const bare = (l: string | undefined) => (l ?? "").replace(QUOTE_MARK, "");
  for (let i = 0; i < lines.length; i++) {
    const line = bare(lines[i]);
    if (SEPARATOR.test(line) || RULE.test(line)) {
      return i;
    }
    // "On … wrote:" may be wrapped over two lines.
    if (WROTE.test(line) || WROTE.test(`${line} ${bare(lines[i + 1])}`)) {
      return i;
    }
    if (FROM.test(line) && lines.slice(i + 1, i + 1 + HEADER_SPAN).some((l) => SENT.test(bare(l)))) {
      return i;
    }
    // A run of "> " lines to the end (the rest of the message quoted).
    if (lines[i]?.startsWith(">") && lines.slice(i).every((l) => l.startsWith(">") || l.trim() === "")) {
      return i;
    }
  }
  return -1;
}

/** At most one blank line in a row, none at either end. */
function squeeze(s: string): string {
  return s
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Splits `text` into the message, its signature and what it quotes. */
export function parseEmail(text: string): EmailText {
  const clean = text.replace(/\r\n?/g, "\n").replace(CID, "").replace(MAILTO, "");
  const lines = clean.split("\n");
  const q = quoteStart(lines);
  const own = q < 0 ? lines : lines.slice(0, q);
  const quoted = q < 0 ? "" : lines.slice(q).join("\n");
  const sig = own.findIndex((l) => l === "-- " || l === "--");
  return {
    body: squeeze((sig < 0 ? own : own.slice(0, sig)).join("\n")),
    signature: sig < 0 ? "" : squeeze(own.slice(sig + 1).join("\n")),
    quoted: squeeze(quoted.replace(QUOTE_MARKS, "")),
  };
}

/** A piece of text: plain, or a link (with the words shown for it). */
export type Run = string | { href: string; label: string };

/** "Site<https://x.com>": Outlook's link with its words; else a bare URL. Trailing punctuation stays text. */
const LINK = /([^\s<>]+)<(https?:\/\/[^>\s]+)>|<?(https?:\/\/[^\s<>"]+)>?/gi;
const TRAILING = /[.,;:!?)\]'"]+$/;

/** `text` with its links as links. */
export function linkify(text: string): Run[] {
  const runs: Run[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK)) {
    const at = m.index;
    runs.push(text.slice(last, at));
    if (m[2]) {
      runs.push({ href: m[2], label: m[1] === m[2] ? m[2] : (m[1] ?? m[2]) });
      last = at + m[0].length;
    } else {
      // "<https://…>" is a link whole; a bare one leaves its trailing punctuation as text.
      const bracketed = m[0].startsWith("<") && m[0].endsWith(">");
      if (m[0].startsWith("<") && !bracketed) {
        runs.push("<");
      }
      const url = m[3] ?? "";
      const href = bracketed ? url : url.replace(TRAILING, "");
      runs.push({ href, label: href });
      last = at + m[0].length - (url.length - href.length);
    }
  }
  runs.push(text.slice(last));
  return runs.filter((r) => r !== "");
}

/** An email's addresses field (`from`, `to`, `cc`): one or a list. */
const mailList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : typeof v === "string" ? [v] : []);

/** Addresses as one line ("Ana <ana@…>, Bia"). */
export const mailText = (v: unknown): string => mailList(v).join(", ");

/** When an email was sent (its `date`), if it says. */
export const mailDate = (data: Readonly<Record<string, unknown>> | null | undefined): string | null => {
  const date = data?.["date"];
  return typeof date === "string" ? date : null;
};
